import { useState } from 'react';
import { ENVELOPE_PRESETS } from '../../av/envelope';
import type { ExtractedEvents } from '../../av/events';
import { mappingTriggers } from '../../av/reactions';
import * as av from '../../domain/avOps';
import { EVENT_CATEGORIES, OBJECT_REACTIONS, POSE_REACTION_PRESETS, SCENE_REACTIONS, type AvMapping, type AvSession, type EventCategory, type Project, type ReactionType } from '../../domain/schemas';
import type { SyncSelectionState, SyncSelectionStore } from '../../state/syncSelection';
import type { EditFn } from '../scene/useSceneEdit';
import { Button, inputClass } from '../ui';

const REACTIONS: ReactionType[] = [...OBJECT_REACTIONS, ...SCENE_REACTIONS];
const UNIT: Record<ReactionType, string> = {
  opacity: 'Δ opacity (−1…1)',
  scale: 'Δ scale (0.2 = ×1.2)',
  position: 'pixels',
  rotation: 'degrees',
  tint: 'strength (0…1)',
  pose: 'strength (×)',
  visibility: '—',
  flash: 'strength (0…1)',
  background: 'strength (0…1)',
  shake: 'pixels',
};

/**
 * Reactions of a session (Phase 4A): which events (filter) drive which change (reaction) of
 * which target, shaped by an envelope. Reactions are reactive overlays — they never change
 * the scene, the animation's keyframes or the music. Every edit is one undo step (typing in
 * a number box merges into one step).
 */
export function MappingEditor({ project, session, events, sel, selection, edit }: { project: Project; session: AvSession; events: ExtractedEvents; sel: SyncSelectionState; selection: SyncSelectionStore; edit: EditFn }) {
  const scene = project.scenes.find((s) => s.id === session.sceneId)!;
  const [reaction, setReaction] = useState<ReactionType>('scale');
  const [category, setCategory] = useState<EventCategory>('kick');
  const [target, setTarget] = useState<string>(scene.objects.find((o) => /sign/i.test(o.name))?.id ?? scene.objects[0]?.id ?? '');
  const scenic = (SCENE_REACTIONS as readonly string[]).includes(reaction);
  const selected = session.mappings.find((m) => m.id === sel.mappingId) ?? null;
  const add = () => {
    let objectId = scenic ? null : target;
    if (reaction === 'pose' && scene.objects.find((o) => o.id === objectId)?.type !== 'character') objectId = scene.objects.find((o) => o.type === 'character')?.id ?? objectId;
    const m = av.defaultMapping(reaction, [category], objectId);
    if (edit(`Add ${av.REACTION_LABELS[reaction].toLowerCase()}`, (p) => av.addMapping(p, session.id, m))) selection.update({ mappingId: m.id });
  };
  return (
    <div className="flex flex-col gap-3">
      <div className="grid gap-2 sm:grid-cols-[1fr_1fr_1fr_auto] sm:items-end">
        <Field label="When">
          <select className={inputClass} value={category} onChange={(e) => setCategory(e.target.value as EventCategory)} data-testid="mapping-new-category">
            {EVENT_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {av.CATEGORY_LABELS[c]} ({events.byCategory.get(c)?.length ?? 0})
              </option>
            ))}
          </select>
        </Field>
        <Field label="Reaction">
          <select className={inputClass} value={reaction} onChange={(e) => setReaction(e.target.value as ReactionType)} data-testid="mapping-new-reaction">
            {REACTIONS.map((r) => (
              <option key={r} value={r}>
                {av.REACTION_LABELS[r]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Target">
          <select className={inputClass} value={scenic ? '' : target} disabled={scenic} onChange={(e) => setTarget(e.target.value)} data-testid="mapping-new-target">
            {scenic && <option value="">Whole scene</option>}
            {scene.objects.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </Field>
        <Button variant="primary" onClick={add} disabled={session.mappings.length >= 32} data-testid="mapping-add">
          Add reaction
        </Button>
      </div>
      {session.mappings.length === 0 ? (
        <p className="text-sm text-zinc-400">No reactions yet. Pick an event, a reaction and a target, or type “pulse the neon sign on every kick”.</p>
      ) : (
        <ul className="flex flex-col gap-1" aria-label="Reactions" data-testid="mapping-list">
          {session.mappings.map((m) => {
            const count = mappingTriggers(m, events).length;
            const name = m.target.kind === 'scene' ? 'scene' : (scene.objects.find((o) => o.id === (m.target as { objectId: string }).objectId)?.name ?? '?');
            return (
              <li key={m.id} className={`flex flex-wrap items-center gap-2 rounded-md px-2 py-1 text-sm ${m.id === sel.mappingId ? 'bg-violet-950/60 ring-1 ring-violet-700' : 'bg-zinc-800/50'}`} data-testid="mapping-row" data-mapping-id={m.id}>
                <input type="checkbox" checked={m.enabled} aria-label={`Enable ${m.name}`} onChange={(e) => edit(e.target.checked ? 'Enable reaction' : 'Disable reaction', (p) => av.updateMapping(p, session.id, m.id, { enabled: e.target.checked }))} />
                <button type="button" className="min-w-0 flex-1 truncate text-left" onClick={() => selection.update({ mappingId: m.id === sel.mappingId ? null : m.id })} aria-pressed={m.id === sel.mappingId}>
                  {m.name} <span className="text-xs text-zinc-400">→ {name} · {count} triggers</span>
                </button>
                <Button size="sm" variant="ghost" onClick={() => edit('Duplicate reaction', (p) => av.duplicateMapping(p, session.id, m.id))}>
                  Duplicate<span className="sr-only"> {m.name}</span>
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    if (edit('Remove reaction', (p) => av.removeMapping(p, session.id, m.id)) && sel.mappingId === m.id) selection.update({ mappingId: null });
                  }}
                  data-testid="mapping-remove"
                >
                  Remove<span className="sr-only"> {m.name}</span>
                </Button>
              </li>
            );
          })}
        </ul>
      )}
      {selected && <MappingDetails key={selected.id} project={project} session={session} mapping={selected} edit={edit} />}
    </div>
  );
}

function MappingDetails({ project, session, mapping: m, edit }: { project: Project; session: AvSession; mapping: AvMapping; edit: EditFn }) {
  const scene = project.scenes.find((s) => s.id === session.sceneId)!;
  const set = (label: string, patch: Partial<AvMapping>, key?: string) => edit(label, (p) => av.updateMapping(p, session.id, m.id, patch), key ? `map:${m.id}:${key}` : undefined);
  const scenic = m.target.kind === 'scene';
  const num = (label: string, value: number, onValue: (v: number) => void, opts: { min: number; max: number; step: number; testid?: string }) => (
    <Field label={label}>
      <input
        className={inputClass}
        type="number"
        min={opts.min}
        max={opts.max}
        step={opts.step}
        value={value}
        data-testid={opts.testid}
        onChange={(e) => {
          const v = Number(e.target.value);
          if (e.target.value !== '' && Number.isFinite(v)) onValue(Math.min(opts.max, Math.max(opts.min, v)));
        }}
      />
    </Field>
  );
  return (
    <fieldset className="grid gap-2 rounded-lg border border-violet-900/60 p-3 sm:grid-cols-3" data-testid="mapping-details">
      <legend className="px-1 text-xs font-semibold text-violet-200">Edit “{m.name}”</legend>
      <Field label="Name">
        <input className={inputClass} value={m.name} maxLength={60} onChange={(e) => e.target.value.trim() && set('Rename reaction', { name: e.target.value }, 'name')} />
      </Field>
      <Field label="Reaction">
        <select
          className={inputClass}
          value={m.reaction}
          onChange={(e) => {
            const r = e.target.value as ReactionType;
            const d = av.defaultMapping(r, m.source.categories, m.target.kind === 'object' ? m.target.objectId : (scene.objects[0]?.id ?? null), m.id);
            set('Change reaction', { reaction: r, params: d.params, target: d.target });
          }}
        >
          {REACTIONS.map((r) => (
            <option key={r} value={r}>
              {av.REACTION_LABELS[r]}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Target">
        <select className={inputClass} value={scenic ? '' : m.target.kind === 'object' ? m.target.objectId : ''} disabled={scenic} onChange={(e) => set('Change target', { target: { kind: 'object', objectId: e.target.value } })}>
          {scenic && <option value="">Whole scene</option>}
          {scene.objects.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
      </Field>
      <fieldset className="sm:col-span-3">
        <legend className="text-xs text-zinc-400">Events (filter)</legend>
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
          {EVENT_CATEGORIES.map((c) => (
            <label key={c} className="flex items-center gap-1 text-xs text-zinc-300">
              <input
                type="checkbox"
                checked={m.source.categories.includes(c)}
                onChange={(e) => {
                  const categories = e.target.checked ? [...m.source.categories, c] : m.source.categories.filter((x) => x !== c);
                  if (categories.length) set('Change events', { source: { ...m.source, categories } });
                }}
              />
              {av.CATEGORY_LABELS[c]}
            </label>
          ))}
        </div>
      </fieldset>
      {num('Every Nth event', m.source.every, (v) => set('Change every', { source: { ...m.source, every: Math.round(v) } }, 'every'), { min: 1, max: 256, step: 1 })}
      {num('Starting at (0 = first)', m.source.phase, (v) => set('Change phase', { source: { ...m.source, phase: Math.round(v) } }, 'phase'), { min: 0, max: 255, step: 1 })}
      {num('Min velocity', m.source.minVelocity, (v) => set('Change velocity filter', { source: { ...m.source, minVelocity: v } }, 'minVelocity'), { min: 0, max: 1, step: 0.05 })}
      <Field label="Section">
        <select className={inputClass} value={m.source.sectionId ?? ''} onChange={(e) => set('Change section', { source: { ...m.source, sectionId: e.target.value || null } })}>
          <option value="">Anywhere</option>
          {project.arrangement.sections.map((s, i) => (
            <option key={s.id} value={s.id}>
              {i + 1}. {s.name}
            </option>
          ))}
        </select>
      </Field>
      {m.reaction !== 'visibility' && num(`Strength · ${UNIT[m.reaction]}`, m.params.amount, (v) => set('Change strength', { params: { ...m.params, amount: v } }, 'amount'), { min: -360, max: 2000, step: 0.01, testid: 'mapping-amount' })}
      {m.reaction === 'position' && (
        <Field label="Direction">
          <select className={inputClass} value={m.params.direction ?? 'up'} onChange={(e) => set('Change direction', { params: { ...m.params, direction: e.target.value as 'up' } })}>
            {['up', 'down', 'left', 'right'].map((d) => (
              <option key={d}>{d}</option>
            ))}
          </select>
        </Field>
      )}
      {(m.reaction === 'tint' || m.reaction === 'flash' || m.reaction === 'background') && (
        <Field label="Colour">
          <input type="color" className="h-9 w-full cursor-pointer rounded border border-zinc-700 bg-transparent" value={m.params.color ?? '#ffffff'} onChange={(e) => set('Change colour', { params: { ...m.params, color: e.target.value } }, 'color')} />
        </Field>
      )}
      {m.reaction === 'pose' && (
        <Field label="Pose">
          <select className={inputClass} value={m.params.pose ?? 'arms-up'} onChange={(e) => set('Change pose', { params: { ...m.params, pose: e.target.value as 'arms-up' } })}>
            {POSE_REACTION_PRESETS.map((p) => (
              <option key={p}>{p}</option>
            ))}
          </select>
        </Field>
      )}
      {m.reaction === 'visibility' && (
        <Field label="Mode">
          <select className={inputClass} value={m.params.mode ?? 'toggle'} onChange={(e) => set('Change visibility mode', { params: { ...m.params, mode: e.target.value as 'toggle' } })}>
            <option value="toggle">toggle on each trigger</option>
            <option value="show">show while active</option>
            <option value="hide">hide while active</option>
          </select>
        </Field>
      )}
      <Field label="Envelope">
        <select className={inputClass} value={m.envelope.shape} onChange={(e) => set('Change envelope', { envelope: { ...ENVELOPE_PRESETS[e.target.value as 'instant'] } })} data-testid="mapping-envelope">
          <option value="instant">instant pulse</option>
          <option value="smooth">smooth pulse</option>
          <option value="impulse">impulse</option>
          <option value="hold">sustained hold</option>
        </select>
      </Field>
      {num('Attack (s)', m.envelope.attack, (v) => set('Change attack', { envelope: { ...m.envelope, attack: v } }, 'attack'), { min: 0, max: 2, step: 0.01 })}
      {num('Hold (s)', m.envelope.hold, (v) => set('Change hold', { envelope: { ...m.envelope, hold: v } }, 'hold'), { min: 0, max: 8, step: 0.01 })}
      {num('Decay (s)', m.envelope.decay, (v) => set('Change decay', { envelope: { ...m.envelope, decay: v } }, 'decay'), { min: 0, max: 8, step: 0.01 })}
      <label className="flex items-center gap-2 self-end pb-2 text-xs text-zinc-300">
        <input type="checkbox" checked={m.envelope.easedRecovery} onChange={(e) => set('Change recovery', { envelope: { ...m.envelope, easedRecovery: e.target.checked } })} />
        Eased recovery
      </label>
      {num('Offset (beats)', m.offsetBeats, (v) => set('Change offset', { offsetBeats: v }, 'offset'), { min: -16, max: 16, step: 0.25 })}
      <Field label="Snap to">
        <select className={inputClass} value={m.quantize} onChange={(e) => set('Change snapping', { quantize: Number(e.target.value) as 0 })}>
          <option value={0}>no snapping</option>
          <option value={1}>beats</option>
          <option value={2}>eighths</option>
          <option value={3}>triplets</option>
          <option value={4}>sixteenths</option>
        </select>
      </Field>
      {num('Cooldown (beats)', m.cooldownBeats, (v) => set('Change cooldown', { cooldownBeats: v }, 'cooldown'), { min: 0, max: 64, step: 0.25 })}
      {num('Velocity response (0–1)', m.velocitySensitivity, (v) => set('Change velocity response', { velocitySensitivity: v }, 'vel'), { min: 0, max: 1, step: 0.05 })}
      {m.reaction === 'shake' && num('Seed', m.seed, (v) => set('Change seed', { seed: Math.round(v) }, 'seed'), { min: 0, max: 2 ** 31 - 1, step: 1 })}
    </fieldset>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex min-w-0 flex-col gap-1 text-xs text-zinc-400">
      {label}
      {children}
    </label>
  );
}
